import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogActions, DialogContent, Typography,
  TablePagination, InputAdornment, TextField, Box, Paper,
  IconButton, Divider, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import api from "../api";
import AgregarMantenimiento from "./utilities/AgregarMantenimiento";
import EditarMantenimiento from "./utilities/EditarMantenimiento";
import DetalleMantenimiento from "./utilities/DetalleMantenimiento"; // <-- Add this import
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from "xlsx";
import DownloadIcon from '@mui/icons-material/Download';

const Mantenimiento = () => {
  const [mantenimiento, setMantenimiento] = useState([]);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMantenimiento, setSelectedMantenimiento] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuMantenimiento, setMenuMantenimiento] = useState(null);

  useEffect(() => {
    fetchMantenimiento();
  }, []);

  const fetchMantenimiento = () => {
    api.get("mantenimiento/")
      .then((response) => {
        setMantenimiento(response.data);
      })
      .catch((error) => {
        console.error("Error al obtener mantenimiento:", error);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este mantenimiento?")) {
      api.delete(`mantenimiento/${id}/`)
        .then(() => {
          setMantenimiento(mantenimiento.filter(m => m.id !== id));
        })
        .catch(error => {
          console.error("Error al eliminar el mantenimiento:", error);
        });
    }
  };

  const handleMenuOpen = (event, mantenimiento) => {
    event.stopPropagation();  // Prevent the row click event from firing
    setAnchorEl(event.currentTarget);
    setMenuMantenimiento(mantenimiento);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuMantenimiento(null);
  };

  const handleEditOpen = () => {
    setOpenEdit(true);
    setAnchorEl(null);
  };

  const handleRowClick = (mantenimiento) => {
    setSelectedMantenimiento(mantenimiento); // Update with the selected mantenimiento
  };

  const handleDetailClose = () => {
    setSelectedMantenimiento(null); // Reset the selected mantenimiento when closing
  };

  const handleMantenimientoUpdated = (updatedMantenimiento) => {
    setMantenimiento((prevMantenimientos) => {
      const exists = prevMantenimientos.some((i) => i.id === updatedMantenimiento.id);
      if (exists) {
        return prevMantenimientos.map((i) =>
          i.id === updatedMantenimiento.id ? updatedMantenimiento : i
        );
      } else {
        return [...prevMantenimientos, updatedMantenimiento];
      }
    });

    setOpen(false);      // para AgregarInfraccion
    setOpenEdit(false);  // para EditarInfraccion
  };

  const normalize = (str) =>
    (str || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  // Helper to remove spaces for tramix_nro search
  const removeSpaces = (str) => (str || "").replace(/\s+/g, "");
  // Helper to remove spaces and dashes for nro_factura search
  const removeSpacesAndDashes = (str) => (str || "").replace(/[\s-]+/g, "");

  const search = normalize(searchTerm);
  const searchNoSpaces = removeSpaces(search);
  const searchNoSpacesDashes = removeSpacesAndDashes(search);

  const filteredMantenimiento = mantenimiento.filter((item) => {
    const fechaFormateada = new Date(item.fecha_requerimiento).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    // monto as string (if exists)
    const montoTexto = item.monto != null ? item.monto.toString() : "";

    // abonado as keywords (add all variants you expect user might type)
    const abonadoTexto = item.abonado
      ? "pagado pagada"
      : "impago impaga";

    // Ensure nro_factura is a trimmed string for search
    const nroFacturaTexto = (item.nro_factura || "").toString().trim();
    const solicitudTexto = (item.solicitud || "").toString().trim();

    return (
      normalize(item.vehiculo_nombre).includes(search) ||
      //normalize(item.descripcion).includes(search) ||
      normalize(fechaFormateada).includes(search) ||
      normalize(montoTexto).includes(search) ||
      normalize(abonadoTexto).includes(search) ||
      // Search tramix_nro: match both with and without spaces
      normalize(item.tramix_nro).includes(search) ||
      removeSpaces(normalize(item.tramix_nro)).includes(searchNoSpaces) ||
      // Search nro_factura: match both with and without spaces/dashes
      normalize(nroFacturaTexto).includes(search) ||
      removeSpacesAndDashes(normalize(nroFacturaTexto)).includes(searchNoSpacesDashes) ||
      normalize(item.taller).includes(search) ||
      normalize(solicitudTexto).includes(search)
    );
  });

  // Download all records as XLS
  const handleDownloadXLS = async () => {
    try {
      const response = await api.get("mantenimiento/"); // Adjust endpoint if needed for all records
      const allData = response.data;

      // Convert to worksheet
      const worksheet = XLSX.utils.json_to_sheet(allData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Mantenimientos");

      // Download
      XLSX.writeFile(workbook, "mantenimiento.xlsx");
    } catch (error) {
      alert("Error al descargar los datos.");
      console.error(error);
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700} color='#152d51'>
          Mantenimiento
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            variant="outlined"
            label="Buscar mantenimiento"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button onClick={handleDownloadXLS} variant="outlined" color="success">
            Descargar XLS&nbsp;<DownloadIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
          <Button onClick={() => setOpen(true)} style={{ border: "1px solid #3f51b5", borderRadius: "6px", color: "#3f51b5" }}>
            Agregar Mantenimiento <AddIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "rgb(244, 246, 248)" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Vehículo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fecha requerimiento</TableCell>
              {/*<TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>*/}
              <TableCell sx={{ fontWeight: 'bold' }}>Monto</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>¿Abonado?</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>N° Tramix</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>N° Factura</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Taller</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Solicitud</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMantenimiento
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((mantenimiento) => (
                <TableRow
                  key={mantenimiento.id}
                  sx={{ '&:hover': { backgroundColor: '#f1f1f1', cursor: 'pointer' } }}
                  onClick={() => handleRowClick(mantenimiento)}
                >
                  <TableCell>{mantenimiento.vehiculo_nombre}</TableCell>
                  <TableCell>
                    {mantenimiento.fecha_requerimiento
                      ? new Date(mantenimiento.fecha_requerimiento).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                      : "—"}
                  </TableCell>
                  {/*<TableCell>{mantenimiento.descripcion}</TableCell>*/}
                  <TableCell>{mantenimiento.monto ? `$${mantenimiento.monto}` : "-"}</TableCell>
                  <TableCell>{mantenimiento.abonado ? "Sí" : "No"}</TableCell>
                  <TableCell>{mantenimiento.tramix_nro}</TableCell>
                  <TableCell>{mantenimiento.nro_factura}</TableCell>
                  <TableCell>{mantenimiento.taller}</TableCell>
                  <TableCell>{mantenimiento.solicitud}</TableCell>
                  <TableCell>
                    <IconButton onClick={(event) => handleMenuOpen(event, mantenimiento)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={mantenimiento.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditOpen}>
          <EditIcon color="primary" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(menuMantenimiento.id); handleMenuClose(); }}>
          <DeleteIcon color="error" sx={{ mr: 1 }} /> Eliminar
        </MenuItem>
      </Menu>

      {/* Dialog for Detail */}
      <Dialog open={!!selectedMantenimiento} onClose={handleDetailClose} fullWidth maxWidth="sm">
        <DialogContent>
          <DetalleMantenimiento selectedMantenimiento={selectedMantenimiento} handleDetailClose={handleDetailClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose} color="secondary">Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <AgregarMantenimiento handleClose={() => setOpen(false)} onUpdate={handleMantenimientoUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <EditarMantenimiento mantenimiento={menuMantenimiento} handleClose={() => setOpenEdit(false)} onUpdate={handleMantenimientoUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Mantenimiento;
